import React from "react";

// Components

// Stores

// Styles
import classes from "./classes.module.scss";
import { Toasts, IToast } from "@Front/Stores/Toasts";
import Typography, { ETypo, ETypoColor } from "@Front/Components/DesignSystem/Typography";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { NextRouter, useRouter } from "next/router";
import { parseDate } from "@Front/Utils/formatters";
import { COMMON_UI_I18N } from "@Front/Components/DesignSystem/helpers/commonUiI18n";
import { onClickToKeyDownHandler } from "@Front/Utils/interactiveElementA11y";

type IProps = {
	toast: IToast;
};

type IPropsClass = IProps & {
	router: NextRouter;
};

type IState = {
	willClose: boolean;
};

class ToastElementClass extends React.Component<IPropsClass, IState> {
	private closeTimeout = 0;
	private rootRef = React.createRef<HTMLDivElement>();

	constructor(props: IPropsClass) {
		super(props);

		this.state = {
			willClose: false,
		};

		this.onClose = this.onClose.bind(this);
		this.handleClick = this.handleClick.bind(this);
	}

	public override render(): React.JSX.Element {
		const toast = this.props.toast;
		const isClickable = Boolean(toast.redirectUrl);
		const toastAriaLabel =
			typeof toast.title === "string" && toast.title.trim().length > 0
				? toast.title.trim()
				: COMMON_UI_I18N.iconButtonAccessibilityLabels.toastOpenLinkedContent;
		const interactiveProps: React.HTMLAttributes<HTMLDivElement> = isClickable
			? {
					role: "button",
					tabIndex: 0,
					onClick: this.handleClick,
					onKeyDown: onClickToKeyDownHandler(this.handleClick),
					"aria-label": toastAriaLabel,
				}
			: {};

		return (
			<div
				ref={this.rootRef}
				className={classes["root"]}
				data-will-close={this.state.willClose}
				data-clickable={isClickable}
				{...interactiveProps}>
				{toast.time !== 0 && <div className={classes["loadbar"]} />}
				{this.renderToastHeader(toast)}
				{toast.button}
			</div>
		);
	}

	private renderToastHeader(toast: IToast): React.JSX.Element {
		return (
			<div className={classes["header"]}>
				<div className={classes["text-icon_row"]}>
					{toast.icon && toast.icon}
					<div className={classes["text-container"]}>
						{this.getToastTitle(toast.title)}
						{this.getToastText(toast.text)}
						{this.getToastDate(toast.created_at)}
					</div>
				</div>
				<div className={classes["header-right"]}>
					{toast.closable !== false && (
						<button
							type="button"
							className={classes["close-button"]}
							onClick={this.onClose}
							aria-label={COMMON_UI_I18N.iconButtonAccessibilityLabels.closeNotification}>
							<XMarkIcon className={classes["close-icon"]} aria-hidden />
						</button>
					)}
					<div className={classes["notif-unread"]}>
						<div className={classes["notif-unread-dot"]} />
					</div>
				</div>
			</div>
		);
	}

	public override componentDidMount(): void {
		this.applyDurationCssVar();
		if (!this.props.toast.time || this.props.toast.time === 0) return;

		this.closeTimeout = window.setTimeout(() => {
			this.close();
		}, this.props.toast.time);
	}

	public override componentDidUpdate(prevProps: IPropsClass): void {
		if (prevProps.toast.time !== this.props.toast.time) {
			this.applyDurationCssVar();
		}
	}

	private applyDurationCssVar(): void {
		const el = this.rootRef.current;
		if (!el) return;
		const ms = this.props.toast.time;
		if (ms && ms !== 0) {
			el.style.setProperty("--data-duration", `${ms}ms`);
		} else {
			el.style.removeProperty("--data-duration");
		}
	}

	public override componentWillUnmount(): void {
		window.clearTimeout(this.closeTimeout);
	}

	private getToastTitle(title: string | React.ReactNode): React.ReactNode {
		if (typeof title === "string") {
			return <Typography typo={ETypo.TEXT_MD_REGULAR}>{title}</Typography>;
		}
		return title;
	}

	private getToastDate(date: Date | string | null | undefined): string | null {
		const parsedDate = parseDate(date);
		if (!parsedDate) {
			return null;
		}
		return parsedDate.toDateString();
	}

	private getToastText(text: React.ReactNode): React.ReactNode {
		if (typeof text === "string") {
			return (
				<div className={classes["text-container"]}>
					<Typography typo={ETypo.TEXT_SM_REGULAR} color={ETypoColor.COLOR_NEUTRAL_500}>
						{text}
					</Typography>
				</div>
			);
		}
		return text;
	}

	private onClose(e: React.MouseEvent): void {
		e.preventDefault();
		e.stopPropagation();
		this.close();
	}

	private async close(): Promise<void> {
		await Toasts.getInstance().markRead(this.props.toast);
		window.clearTimeout(this.closeTimeout);
		this.setState({
			willClose: true,
		});
		setTimeout(() => {
			Toasts.getInstance().close(this.props.toast);
		}, 200);
	}

	private async handleClick(e: React.MouseEvent): Promise<void> {
		if (this.props.toast.redirectUrl) {
			await this.onClose(e);
			await this.props.router.push(this.props.toast.redirectUrl);
			this.props.router.reload();
		}
	}
}

export default function ToastElement(props: IProps): React.JSX.Element {
	const router = useRouter();
	return <ToastElementClass {...props} router={router} />;
}
